import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, forkJoin, of, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs/operators';
import { AgreementService } from '../../../../services/agreement.service';
import { Agreement, AgreementItem, AgreementPayment, PendingReceivable, CreateAgreementPayload } from '../../../../models/Agreement';
import { AuthService } from '../../../../services/auth.service';
import { CustomersService } from '../../../../services/customers.service';
import { Customer } from '../../../../models/Customer';
import { AccountsReceivableService } from '../../../../services/accounts-receivable.service';
import { PaymentService } from '../../../../services/payment.service';
import { ErrorService } from '../../../../shared/services/error.service';
import { AccountsReceivable, AccountsReceivablePayment } from '../../../../models/AccountsReceivable';
import { CancelBoletosBatchResponse } from '../../../../models/Payment';
import {
  buildAgreementTitleCancelUpdate,
  canSubstituteReceivableTitleForAgreement,
  extractAsaasChargeIdFromReceivable,
  formatReceivableDateForApi,
  hasActiveReceivableCharge,
  isReceivablePaymentCancellable
} from '../../../../utils/accounts-receivable.utils';

interface ReceivableBoleto extends AccountsReceivablePayment {
  cancelSelected: boolean;
  /** Cobrança gravada no próprio título (sem linha em /payments). */
  titleCharge?: boolean;
}

interface SelectedReceivable extends PendingReceivable {
  selected: boolean;
  agreedAmount: number;
  boletos: ReceivableBoleto[];
  loadingBoletos: boolean;
  boletosExpanded: boolean;
  titleDetail?: AccountsReceivable;
}

interface AgreementCancelPlan {
  paymentIds: number[];
  asaasChargeIds: string[];
  titlesToCancel: AccountsReceivable[];
}

@Component({
  selector: 'app-agreement-form',
  templateUrl: './agreement-form.component.html',
  styleUrls: ['./agreement-form.component.css']
})
export class AgreementFormComponent implements OnInit, OnDestroy {
  isViewMode = false;
  isEditMode = false;
  loading = false;
  saving = false;
  error = '';
  successMessage = '';

  agreement: Partial<Agreement> = {
    status: 'Pendente',
    installments: 1,
    discount: 0,
    totalOriginalAmount: 0,
    totalAgreedAmount: 0
  };
  payments: AgreementPayment[] = [];

  // For create mode — customer selection
  customerId: number | null = null;
  customerSearchTerm = '';
  customerResults: Customer[] = [];
  loadingCustomers = false;
  selectedCustomer: Customer | null = null;
  showCustomerDropdown = false;
  customerListTotal = 0;
  private readonly customerPageSize = 300;

  pendingReceivables: SelectedReceivable[] = [];
  loadingReceivables = false;

  private customerSearch$ = new Subject<string>();
  private searchSub?: Subscription;

  installments = 1;
  dueDate = '';
  notes = '';
  terms = '';

  /** Desconto em % (0–100), sincronizado com o valor em R$. */
  discountPercent = 0;
  private syncingDiscount = false;

  // For pay modal
  selectedPaymentId: number | null = null;
  paymentMethod = 'Dinheiro';
  receipt = '';

  paymentMethods = ['Dinheiro', 'PIX', 'Cartão de Débito', 'Cartão de Crédito', 'Boleto', 'Transferência'];

  private currentUser = '';

  cancellingBoletos = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private agreementService: AgreementService,
    private authService: AuthService,
    private customersService: CustomersService,
    private accountsReceivableService: AccountsReceivableService,
    private paymentService: PaymentService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.currentUser =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      this.authService.currentUserValue?.email ||
      'system';
    const id = this.route.snapshot.paramMap.get('id');
    const mode = this.route.snapshot.data['mode'];

    if (id && id !== 'new') {
      this.isViewMode = mode === 'view' || !mode;
      this.isEditMode = mode === 'edit';
      this.loadAgreement(+id);
    } else {
      // Create mode — read query params for context
      this.customerId = this.route.snapshot.queryParams['customerId'] ? +this.route.snapshot.queryParams['customerId'] : null;
      this.agreement.customerId = this.customerId ?? undefined;

      if (this.customerId) {
        this.customersService.getById(this.customerId).subscribe(c => {
          this.selectedCustomer = c;
          this.customerSearchTerm = c.name;
          this.loadPendingReceivables(this.customerId!);
        });
      }

      this.searchSub = this.customerSearch$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => this.fetchCustomers(term))
      ).subscribe({
        next: (result) => {
          this.customerResults = result.data ?? [];
          this.customerListTotal = result.totalRecords ?? this.customerResults.length;
          this.loadingCustomers = false;
          this.showCustomerDropdown = true;
        },
        error: () => {
          this.loadingCustomers = false;
          this.customerResults = [];
          this.customerListTotal = 0;
        }
      });
    }
  }

  private fetchCustomers(term: string) {
    this.loadingCustomers = true;
    const search = (term || '').trim();
    const options = { page: 1, pageSize: this.customerPageSize };
    if (search.length >= 1) {
      return this.customersService.getAllPaged({ ...options, searchTerm: search });
    }
    return this.customersService.getAllPaged(options);
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  /** Ao clicar ou focar no campo: lista todos os clientes (depois filtra pela busca). */
  onCustomerFieldActivate(): void {
    if (this.selectedCustomer) return;
    this.showCustomerDropdown = true;
    this.customerSearch$.next(this.customerSearchTerm.trim());
  }

  onCustomerSearchInput(): void {
    if (this.selectedCustomer) return;
    this.showCustomerDropdown = true;
    this.customerSearch$.next(this.customerSearchTerm.trim());
  }

  onCustomerFieldBlur(): void {
    setTimeout(() => {
      this.showCustomerDropdown = false;
    }, 220);
  }

  selectCustomer(customer: Customer): void {
    this.selectedCustomer = customer;
    this.customerId = customer.id;
    this.customerSearchTerm = customer.name;
    this.agreement.customerId = customer.id;
    this.showCustomerDropdown = false;
    this.customerResults = [];
    this.pendingReceivables = [];
    this.loadPendingReceivables(customer.id);
  }

  clearCustomer(): void {
    this.selectedCustomer = null;
    this.customerId = null;
    this.customerSearchTerm = '';
    this.agreement.customerId = undefined;
    this.pendingReceivables = [];
  }

  get isCreateMode(): boolean {
    return !this.isViewMode && !this.isEditMode;
  }

  get hasSelectedReceivables(): boolean {
    return this.pendingReceivables.some(r => r.selected);
  }

  get selectedTitlesCount(): number {
    return this.pendingReceivables.filter(r => r.selected).length;
  }

  get selectedBoletosCount(): number {
    return this.buildCancelPlan().paymentIds.length;
  }

  get selectedCustomerLabel(): string {
    return this.selectedCustomer?.name || '';
  }

  loadAgreement(id: number): void {
    this.loading = true;
    this.agreementService.getById(id).subscribe({
      next: (data) => {
        this.agreement = data;
        this.payments = data.payments ?? [];
        if (!(data.customerName || '').trim() && data.customerId) {
          this.customersService.getById(data.customerId).subscribe({
            next: (c) => { this.agreement.customerName = c.name; },
            error: () => { /* mantém vazio */ }
          });
        }
        this.loading = false;
      },
      error: () => {
        this.error = 'Erro ao carregar acordo.';
        this.loading = false;
      }
    });
  }

  loadPendingReceivables(customerId: number): void {
    this.loadingReceivables = true;
    this.agreementService.getPendingReceivablesByCustomer(customerId).subscribe({
      next: (data) => {
        this.pendingReceivables = data.map(r => ({
          ...r,
          selected: false,
          agreedAmount: r.amountToReceive,
          boletos: [],
          loadingBoletos: true,
          boletosExpanded: false
        }));
        this.loadingReceivables = false;
        this.recalcTotals();
        this.loadBoletosForReceivables();
      },
      error: () => {
        this.loadingReceivables = false;
      }
    });
  }

  private loadBoletosForReceivables(): void {
    if (this.pendingReceivables.length === 0) return;

    const requests = this.pendingReceivables.map(r =>
      forkJoin({
        receivableId: of(r.id),
        payments: this.accountsReceivableService.getPaymentsByAccountId(r.id).pipe(
          catchError(() => of([] as AccountsReceivablePayment[]))
        ),
        title: this.accountsReceivableService.getAccountsReceivableById(r.id).pipe(
          catchError(() => of(null as AccountsReceivable | null))
        )
      })
    );

    forkJoin(requests).subscribe({
      next: (results) => {
        for (const { receivableId, payments, title } of results) {
          const row = this.pendingReceivables.find(x => x.id === receivableId);
          if (!row) continue;
          row.titleDetail = title ?? undefined;
          row.boletos = this.buildBoletosForRow(row, payments || []);
          row.loadingBoletos = false;
        }
      },
      error: () => {
        this.pendingReceivables.forEach(r => { r.loadingBoletos = false; });
      }
    });
  }

  private buildBoletosForRow(
    row: SelectedReceivable,
    payments: AccountsReceivablePayment[]
  ): ReceivableBoleto[] {
    const list: ReceivableBoleto[] = payments.map(p => ({
      ...p,
      cancelSelected: false,
      titleCharge: false
    }));

    if (list.length === 0 && row.titleDetail && hasActiveReceivableCharge(row.titleDetail)) {
      const ar = row.titleDetail;
      list.push({
        id: 0,
        accountsReceivableId: ar.id,
        amount: ar.amountToReceive,
        paymentMethod: ar.receiptMethod || 'Cobrança no título',
        reference: ar.receiptReference,
        paymentDate: formatReceivableDateForApi(ar.issueDate),
        status: 'Pendente',
        cancelSelected: false,
        titleCharge: true
      });
    }
    return list;
  }

  hasTitleSubstitution(r: SelectedReceivable): boolean {
    return r.selected && canSubstituteReceivableTitleForAgreement(r.titleDetail);
  }

  toggleReceivable(r: SelectedReceivable): void {
    r.selected = !r.selected;
    if (r.selected) {
      r.boletosExpanded = true;
      this.syncBoletosSelectionForReceivable(r, true);
    } else {
      this.syncBoletosSelectionForReceivable(r, false);
    }
    this.recalcTotals();
  }

  toggleBoletosExpanded(r: SelectedReceivable): void {
    r.boletosExpanded = !r.boletosExpanded;
  }

  toggleBoletoCancel(r: SelectedReceivable, boleto: ReceivableBoleto): void {
    if (boleto.titleCharge) {
      boleto.cancelSelected = !boleto.cancelSelected;
    } else if (!isReceivablePaymentCancellable(boleto)) {
      return;
    } else {
      boleto.cancelSelected = !boleto.cancelSelected;
    }
    if (boleto.cancelSelected && !r.selected) {
      r.selected = true;
      this.recalcTotals();
    }
  }

  private syncBoletosSelectionForReceivable(r: SelectedReceivable, selected: boolean): void {
    for (const b of r.boletos) {
      if (b.titleCharge || isReceivablePaymentCancellable(b)) {
        b.cancelSelected = selected;
      }
    }
  }

  private buildCancelPlan(): AgreementCancelPlan {
    const paymentIds: number[] = [];
    const asaasSet = new Set<string>();
    const titlesToCancel: AccountsReceivable[] = [];

    for (const r of this.pendingReceivables.filter(x => x.selected)) {
      for (const b of r.boletos) {
        if (!b.cancelSelected) continue;
        if (b.titleCharge) continue;
        if (isReceivablePaymentCancellable(b) && b.id > 0) {
          paymentIds.push(b.id);
        }
      }

      const chargeFromTitle = extractAsaasChargeIdFromReceivable(r.titleDetail);
      if (chargeFromTitle) {
        asaasSet.add(chargeFromTitle);
      }

      for (const b of r.boletos) {
        if (b.cancelSelected) {
          const ref = (b.reference || '').trim();
          if (ref.startsWith('pay_')) asaasSet.add(ref);
        }
      }

      if (r.titleDetail && canSubstituteReceivableTitleForAgreement(r.titleDetail)) {
        titlesToCancel.push(r.titleDetail);
      }
    }

    return {
      paymentIds,
      asaasChargeIds: Array.from(asaasSet),
      titlesToCancel
    };
  }

  isPaymentCancellable(p: ReceivableBoleto): boolean {
    return !!p.titleCharge || isReceivablePaymentCancellable(p);
  }

  formatBoletoLabel(b: ReceivableBoleto): string {
    const ref = (b.reference || '').trim();
    const method = (b.paymentMethod || '').trim();
    const amount = Number(b.amount) || 0;
    const parts = [method || 'Pagamento', amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })];
    if (ref) parts.push(ref);
    return parts.join(' · ');
  }

  recalcTotals(): void {
    const selected = this.pendingReceivables.filter(r => r.selected);
    this.agreement.totalOriginalAmount = selected.reduce((s, r) => s + r.amountToReceive, 0);
    this.agreement.totalAgreedAmount = selected.reduce((s, r) => s + (r.agreedAmount || 0), 0);
    this.agreement.discount = Math.max(0, (this.agreement.totalOriginalAmount ?? 0) - (this.agreement.totalAgreedAmount ?? 0));
    this.syncDiscountPercentFromAmount();
  }

  /** Ex.: 1234.5 → "1.234,50" */
  formatMoneyBr(value: number | null | undefined): string {
    if (value == null || value === undefined || isNaN(Number(value))) {
      return '';
    }
    return Number(value).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /** Digitação estilo caixa: só dígitos, últimos 2 = centavos. */
  onMoneyInput(event: Event, field: 'agreed' | 'discount' | 'line', row?: SelectedReceivable): void {
    const input = event.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D/g, '').slice(0, 15);
    const value = digits === '' ? 0 : parseInt(digits, 10) / 100;
    input.value = this.formatMoneyBr(value);

    if (field === 'agreed') {
      this.agreement.totalAgreedAmount = value;
      this.onAgreedAmountChange();
    } else if (field === 'discount') {
      this.agreement.discount = value;
      this.onDiscountChange();
    } else if (row) {
      row.agreedAmount = value;
      this.recalcTotals();
    }
  }

  onMoneyFocus(event: Event, value: number | null | undefined): void {
    const input = event.target as HTMLInputElement;
    if (value != null && !isNaN(Number(value))) {
      input.value = this.formatMoneyBr(value);
    }
  }

  onAgreedAmountChange(): void {
    if (this.syncingDiscount) return;
    const original = this.getOriginalTotal();
    let agreed = Math.max(0, Number(this.agreement.totalAgreedAmount) || 0);
    if (agreed > original) {
      agreed = original;
      this.agreement.totalAgreedAmount = agreed;
    }
    this.agreement.discount = Math.max(0, original - agreed);
    this.syncDiscountPercentFromAmount();
  }

  /** Desconto em R$ → recalcula total acordado e %. */
  onDiscountChange(): void {
    if (this.syncingDiscount) return;
    const original = this.getOriginalTotal();
    let discount = Math.max(0, Number(this.agreement.discount) || 0);
    if (discount > original) {
      discount = original;
      this.agreement.discount = discount;
    }
    this.agreement.totalAgreedAmount = Math.max(0, original - discount);
    this.syncDiscountPercentFromAmount();
  }

  /** Desconto em % → calcula valor em R$ e total acordado. */
  onDiscountPercentInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = (input.value || '').replace(/%/g, '').trim().replace(',', '.');
    let pct = parseFloat(raw);
    if (isNaN(pct) || raw === '') pct = 0;
    pct = Math.max(0, Math.min(100, pct));
    this.discountPercent = Math.round(pct * 100) / 100;
    input.value = this.formatPercentBr(this.discountPercent);
    this.applyDiscountFromPercent();
  }

  onDiscountPercentFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = this.formatPercentBr(this.discountPercent);
  }

  formatPercentBr(value: number | null | undefined): string {
    if (value == null || isNaN(Number(value))) return '';
    return Number(value).toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  private getOriginalTotal(): number {
    return Number(this.agreement.totalOriginalAmount) || 0;
  }

  private syncDiscountPercentFromAmount(): void {
    const original = this.getOriginalTotal();
    const discount = Number(this.agreement.discount) || 0;
    this.discountPercent =
      original > 0 ? Math.round((discount / original) * 10000) / 100 : 0;
  }

  private applyDiscountFromPercent(): void {
    const original = this.getOriginalTotal();
    const pct = Math.max(0, Math.min(100, Number(this.discountPercent) || 0));
    const discount = Math.round((original * pct) / 100 * 100) / 100;
    this.syncingDiscount = true;
    this.agreement.discount = discount;
    this.agreement.totalAgreedAmount = Math.max(0, Math.round((original - discount) * 100) / 100);
    this.syncingDiscount = false;
  }

  save(): void {
    if (!this.agreement.customerId) {
      this.error = 'Cliente é obrigatório.';
      return;
    }
    if (!this.dueDate) {
      this.error = 'Data de vencimento é obrigatória.';
      return;
    }

    const selectedReceivables = this.pendingReceivables.filter(r => r.selected);
    if (selectedReceivables.length === 0) {
      this.error = 'Selecione ao menos um título para o acordo.';
      return;
    }

    const plan = this.buildCancelPlan();
    const confirmLines = [
      `Títulos no acordo: ${selectedReceivables.length}.`,
      `Títulos a substituir/cancelar: ${plan.titlesToCancel.length}.`,
      plan.paymentIds.length > 0
        ? `Pagamentos/boletos (cancel-batch): ${plan.paymentIds.length}.`
        : null,
      plan.asaasChargeIds.length > 0
        ? `Cobranças Asaas: ${plan.asaasChargeIds.length}.`
        : null,
      'Em seguida será criado o acordo com novas parcelas.',
      'Deseja continuar?'
    ].filter((line): line is string => !!line);
    if (!confirm(confirmLines.join('\n\n'))) return;

    this.saving = true;
    this.error = '';

    const items: Partial<AgreementItem>[] = selectedReceivables.map(r => ({
      sourceType: 'AccountsReceivable',
      sourceId: r.id,
      description: r.description,
      originalAmount: r.amountToReceive,
      agreedAmount: r.agreedAmount
    }));

    const payload: CreateAgreementPayload = {
      agreement: {
        ...this.agreement,
        dueDate: this.dueDate,
        notes: this.notes || undefined,
        terms: this.terms || undefined
      },
      items,
      installments: this.installments,
      createdBy: this.currentUser
    };

    const cancelThenCreate$ =
      plan.titlesToCancel.length > 0 || plan.paymentIds.length > 0 || plan.asaasChargeIds.length > 0
        ? this.executeCancelPlan(plan)
        : of(null);

    cancelThenCreate$.pipe(
      switchMap(() => this.agreementService.create(payload))
    ).subscribe({
      next: (created) => {
        this.saving = false;
        this.cancellingBoletos = false;
        this.router.navigate(['/financial/agreements', created.id]);
      },
      error: (err) => {
        this.saving = false;
        this.cancellingBoletos = false;
        this.error = 'Erro ao cancelar títulos/boletos ou criar acordo.';
        console.error(err);
      }
    });
  }

  /**
   * 1) Asaas em lote; 2) cancel-batch de pagamentos; 3) marca títulos selecionados como Cancelado.
   */
  private executeCancelPlan(plan: AgreementCancelPlan): Observable<unknown> {
    this.cancellingBoletos = true;

    const cancelAsaas$: Observable<CancelBoletosBatchResponse | null> = plan.asaasChargeIds.length > 0
      ? this.paymentService.cancelBoletosBatch(plan.asaasChargeIds)
      : of(null);

    return cancelAsaas$.pipe(
      switchMap((asaasResult) => {
        if (asaasResult && asaasResult.totalFailed > 0) {
          const failedIds = asaasResult.failed.map(f => f.id).join(', ');
          this.errorService.showInfoMessage(
            `Asaas: ${asaasResult.totalCancelled} cancelado(s), ${asaasResult.totalFailed} falhou (${failedIds}). Prosseguindo.`,
            8000
          );
        }
        if (plan.paymentIds.length === 0) {
          return of(null);
        }
        return this.accountsReceivableService.cancelPaymentsBatch(plan.paymentIds, this.currentUser);
      }),
      switchMap((batchResult) => {
        if (batchResult) {
          this.errorService.showInfoMessage(
            `${batchResult.cancelledPayments} pagamento(s) cancelado(s). ${batchResult.affectedReceivables ?? 0} título(s) recalculado(s).`
          );
        }
        if (plan.titlesToCancel.length === 0) {
          return of(null);
        }
        const updates = plan.titlesToCancel.map(ar =>
          this.accountsReceivableService.updateAccountsReceivable(buildAgreementTitleCancelUpdate(ar))
        );
        return forkJoin(updates);
      }),
      map((titleResults) => {
        if (Array.isArray(titleResults) && titleResults.length > 0) {
          this.errorService.showInfoMessage(
            `${titleResults.length} título(s) marcado(s) como cancelado(s) e substituído(s) pelo acordo.`
          );
        }
        return titleResults;
      })
    );
  }

  cancel(): void {
    if (!confirm('Confirmar cancelamento do acordo?')) return;
    this.agreementService.cancel(this.agreement.id!, this.currentUser).subscribe({
      next: () => {
        this.agreement.status = 'Cancelado';
        this.successMessage = 'Acordo cancelado com sucesso.';
      },
      error: () => { this.error = 'Erro ao cancelar acordo.'; }
    });
  }

  openPayModal(paymentId: number): void {
    this.selectedPaymentId = paymentId;
    this.paymentMethod = 'Dinheiro';
    this.receipt = '';
  }

  confirmPayment(): void {
    if (!this.selectedPaymentId) return;
    this.agreementService.markPaymentAsPaid(this.selectedPaymentId, {
      paidBy: this.currentUser,
      paymentMethod: this.paymentMethod,
      receipt: this.receipt || undefined,
      updatedBy: this.currentUser
    }).subscribe({
      next: (updated) => {
        const idx = this.payments.findIndex(p => p.id === updated.id);
        if (idx >= 0) this.payments[idx] = updated;
        this.selectedPaymentId = null;
        if (this.payments.every(p => p.status === 'Pago')) {
          (this.agreement as Agreement).status = 'Quitado';
        }
        this.successMessage = 'Pagamento registrado com sucesso.';
      },
      error: () => { this.error = 'Erro ao registrar pagamento.'; }
    });
  }

  back(): void {
    this.router.navigate(['/financial/agreements']);
  }
}
